<template>
  <vx-card class="w-full">
    <!-- ── 검색 영역 ── -->
    <search-tools v-model="search" @search="loadData">
      <template #default="{ setRef }">
        <search-select-field label="사업장명" :setRef="setRef" v-model="search.field" :options="{ nav: search }" @input="onSelectChange" />
        <search-year label="연도" v-model="search.year" :options="{ default: new Date().getFullYear(), afterFix: '년' }" @input="onSelectChange" />
        <search-month label="월" v-model="search.month" :options="{ default: new Date().getMonth() + 1 }" @input="onSelectChange" />
        <search-keyword label="이름" placeholder="이름" v-model="search.이름" :options="{}" :onClickSearch="loadData" />
        <search-keyword label="기타검색" placeholder="Search" v-model="search.keyword" :options="{}" :onClickSearch="loadData" />
      </template>
    </search-tools>

    <!-- ── perPage + 버튼 영역 ── -->
    <div class="flex items-center justify-between mb-4">
      <vs-select v-model="perPage" @change="onChangePerPage" style="width: 130px">
        <vs-select-item v-for="n in [20, 50, 100]" :key="n" :value="n" :text="`${n}명씩 보기`" />
      </vs-select>
      <div class="flex gap-2">
        <vs-button color="secondary" type="border" @click="onClickExcel">
          <div class="flex items-center gap-1">
            <feather-icon icon="DownloadIcon" svgClasses="h-4 w-4" />
            Excel 다운로드
          </div>
        </vs-button>
        <vs-button color="secondary" type="border" @click="onClickPrint">
          <div class="flex items-center gap-1">
            <feather-icon icon="PrinterIcon" svgClasses="h-4 w-4" />
            인쇄
          </div>
        </vs-button>
      </div>
    </div>

    <!-- ── 로딩 ── -->
    <div v-if="isLoading" class="py-10 text-center">
      <vs-progress indeterminate color="primary" />
      <p class="mt-2 text-gray-500">데이터를 불러오는 중...</p>
    </div>

    <!-- ── 모니터링 테이블 ── -->
    <div v-else ref="scrollContainer" class="overflow-x-auto" :class="{ 'is-dragging': isDragging }" @mousedown="onDragStart" @mousemove="onDragMove" @mouseup="onDragEnd" @mouseleave="onDragEnd">
      <table class="monitor-table" id="monitor-table">
        <colgroup>
          <col style="width: 50px" />
          <col style="width: 100px" />
          <col style="width: 80px" />
          <col style="width: 70px" />
          <col v-for="d in days" :key="`col-${d}`" style="width: 34px" />
        </colgroup>
        <thead>
          <tr>
            <th rowspan="2" class="sticky-col sticky-col-0">No.</th>
            <th rowspan="2" class="sticky-col sticky-col-1">사업장명</th>
            <th rowspan="2" class="sticky-col sticky-col-2">이름</th>
            <th rowspan="2" class="sticky-col sticky-col-3">직급</th>
            <th :colspan="days.length" class="month-header">{{ appliedMonth }}월</th>
          </tr>
          <tr>
            <th v-for="d in days" :key="`th-${d}`" class="day-th" :class="getDayClass(d)">{{ d }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredSupervisors.length === 0">
            <td :colspan="4 + days.length" class="py-10 text-center text-gray-400">데이터가 없습니다.</td>
          </tr>
          <tr v-for="(sup, i) in filteredSupervisors" :key="sup.postId">
            <td class="sticky-col sticky-col-0">{{ (page - 1) * perPage + i + 1 }}</td>
            <td class="sticky-col sticky-col-1">{{ sup.사업장명 }}</td>
            <td class="sticky-col sticky-col-2">{{ sup.이름 }}</td>
            <td class="sticky-col sticky-col-3">{{ sup.직급 }}</td>
            <td v-for="d in days" :key="`cell-${sup.postId}-${d}`" class="day-cell" :class="getCellClass(sup, d)" @click="onClickCell(sup, d)">
              <template v-if="getCellDocs(sup, d).length === 0">
                <span class="dot-empty">●</span>
              </template>
              <template v-else>
                <span class="doc-count">{{ getCellDocs(sup, d).length }}</span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ── 페이징 ── -->
    <div v-if="!isLoading" class="vs-con-table">
      <div class="con-pagination-table vs-table--pagination">
        <k-pagination :total="lastPage ? lastPage : 1" :value="page" @change="onChangePage" />
      </div>
    </div>

    <!-- ── 신규 작성 확인 팝업 ── -->
    <vs-popup title="신규 문서 작성" :active.sync="showNewDocPopup">
      <div v-if="newDocTarget" style="padding: 8px 0">
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px">
          <tbody>
            <tr>
              <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9; width: 80px; text-align: left">사업장</th>
              <td style="border: 1px solid #ddd; padding: 8px">{{ newDocTarget.sup.사업장명 }}</td>
            </tr>
            <tr>
              <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9; text-align: left">이름</th>
              <td style="border: 1px solid #ddd; padding: 8px">{{ newDocTarget.sup.이름 }} ({{ newDocTarget.sup.직급 }})</td>
            </tr>
            <tr>
              <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9; text-align: left">점검일</th>
              <td style="border: 1px solid #ddd; padding: 8px">{{ appliedYear }}년 {{ appliedMonth }}월 {{ newDocTarget.day }}일</td>
            </tr>
          </tbody>
        </table>
        <p style="color: #555; font-size: 13px; margin-bottom: 16px">위 정보로 새 문서를 작성하시겠습니까?<br />작성 페이지는 새 탭에서 열립니다.</p>
        <div style="display: flex; justify-content: flex-end; gap: 8px">
          <vs-button
            type="border"
            color="dark"
            @click="
              showNewDocPopup = false;
              newDocTarget = null;
            "
            >취소</vs-button
          >
          <vs-button color="primary" @click="openNewDoc">새 탭에서 작성</vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- ── 문서 목록 팝업 ── -->
    <vs-popup title="작성 문서 목록" :active.sync="showDocListPopup">
      <table class="w-full" style="border-collapse: collapse">
        <thead>
          <tr>
            <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9">No.</th>
            <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9">점검일</th>
            <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9">점검시간</th>
            <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9">작성일</th>
            <th style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9">열람</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(doc, di) in selectedDocs" :key="doc.postId">
            <td style="border: 1px solid #ddd; padding: 8px; text-align: center">{{ di + 1 }}</td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: center">{{ doc.점검일 }}</td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: center">{{ doc.점검시간 }}</td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: center">{{ doc.작성일 }}</td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: center">
              <vs-button size="small" color="primary" type="border" @click="openDoc(doc)">열람</vs-button>
            </td>
          </tr>
        </tbody>
      </table>
    </vs-popup>
  </vx-card>
</template>

<script>
import moment from "moment";
import ExcelJS from "exceljs";
import excelSupporter from "@/excelSupporter";
import SearchTools from "@/views/list/components/SearchTools.vue";
import KPagination from "@/components/kPagination/kPagination.vue";
import SearchSelectField from "@/views/list/components/SearchSelectField.vue";
import SearchYear from "@/views/list/components/SearchYear.vue";
import SearchMonth from "@/views/list/components/SearchMonth.vue";
import SearchKeyword from "@/views/list/components/SearchKeyword.vue";

export default {
  name: "Page1141",
  components: {
    SearchTools,
    KPagination,
    SearchSelectField,
    SearchYear,
    SearchMonth,
    SearchKeyword,
  },
  props: {
    board: Object,
    permission: {
      type: Object,
      default: () => ({ c: 0, r: 0, u: 0, d: 0, v: 0 }),
    },
  },
  data() {
    return {
      search: {
        field: null,
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        이름: "",
        keyword: "",
      },

      appliedYear: new Date().getFullYear(),
      appliedMonth: new Date().getMonth() + 1,

      supervisors: [], // board 738 데이터
      inspectDocs: [], // board 1140 데이터

      isLoading: false,
      showDocListPopup: false,
      selectedDocs: [],
      showNewDocPopup: false,
      newDocTarget: null,

      page: 1,
      lastPage: 1,
      perPage: 50,

      isDragging: false,
      dragStartX: 0,
      dragScrollLeft: 0,

      autoSearchReady: false,
    };
  },
  computed: {
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
    daysInMonth() {
      return new Date(this.appliedYear, this.appliedMonth, 0).getDate();
    },
    days() {
      return Array.from({ length: this.daysInMonth }, (_, i) => i + 1);
    },
    filteredSupervisors() {
      return this.supervisors;
    },
    // postId -> { day: [docs] }
    docMap() {
      const map = {};
      for (const doc of this.inspectDocs) {
        const id = doc.connect738PostId;
        if (!id) continue;
        const day = parseInt(doc.점검일.split("-")[2]);
        if (!map[id]) map[id] = {};
        if (!map[id][day]) map[id][day] = [];
        map[id][day].push(doc);
      }
      return map;
    },
  },
  created() {
    this.loadData();
    this.$nextTick(() => {
      this.autoSearchReady = true;
    });
  },
  methods: {
    onSelectChange() {
      if (this.autoSearchReady) this.loadData();
    },

    // ─── 데이터 로딩 ────────────────────────────────────────────
    async loadData() {
      if (!this.search.month) {
        this.$vs.notify({ title: "알림", text: "월을 선택해주세요.", color: "warning" });
        return;
      }
      this.appliedYear = this.search.year || new Date().getFullYear();
      this.appliedMonth = this.search.month;
      this.page = 1;
      this.isLoading = true;
      try {
        await this.loadSupervisors();
        await this.loadInspectDocs();
      } finally {
        this.isLoading = false;
      }
    },

    async loadSupervisors() {
      const conditions = [
        { key: "header.year", operator: "=", value: this.appliedYear },
        { key: "body.관리감독자정보.구분", operator: "!=", value: "관리직" },
      ];
      if (this.search.field && this.search.field.fieldId) {
        conditions.push({ key: "body.stdInfo.field_id", operator: "=", value: this.search.field.fieldId });
      }
      if (this.search.이름) {
        conditions.push({ key: "body.관리감독자정보.이름", operator: "LIKE", value: `%${this.search.이름}%` });
      }
      if (this.search.keyword) {
        conditions.push({ key: "body.관리감독자정보.이름", operator: "LIKE", value: `%${this.search.keyword}%`, boolean: "OR" });
        conditions.push({ key: "body.stdInfo.field_name", operator: "LIKE", value: `%${this.search.keyword}%`, boolean: "OR" });
        conditions.push({ key: "body.관리감독자정보.직무", operator: "LIKE", value: `%${this.search.keyword}%`, boolean: "OR" });
        conditions.push({ key: "body.관리감독자정보.부서명", operator: "LIKE", value: `%${this.search.keyword}%`, boolean: "OR" });
      }
      const res = await this.$store.dispatch("board/LOAD_POST_LIST", {
        projectId: this.projectId,
        boardId: 738,
        boardSlug: "board-738",
        page: this.page,
        perPage: this.perPage,
        orderTarget: "created_at",
        orderDirection: "asc",
        conditions,
      });
      this.lastPage = res.last_page || 1;
      this.supervisors = (res.data || []).map((item) => {
        const body = JSON.parse(item.post_content).body;
        const info = body.관리감독자정보 || {};
        return {
          postId: item.id,
          boardId: item.board_id,
          사업장명: (body.stdInfo && body.stdInfo.field_name) || "",
          이름: info.이름 || "",
          직무: info.직무 || "",
          직급: info.직급 || "",
          부서명: info.부서명 || "",
          raw: item,
        };
      });
    },

    async loadInspectDocs() {
      const supervisorIds = this.supervisors.map((s) => s.postId);
      if (supervisorIds.length === 0) {
        this.inspectDocs = [];
        return;
      }

      const startDate = `${this.appliedYear}-${String(this.appliedMonth).padStart(2, "0")}-01`;
      const endDate = moment(startDate).endOf("month").format("YYYY-MM-DD");

      const allDocs = [];
      let page = 1;
      for (;;) {
        const res = await this.$store.dispatch("board/LOAD_POST_LIST", {
          projectId: this.projectId,
          boardId: 1140,
          boardSlug: "board-1140",
          page,
          perPage: 1000,
          orderTarget: "created_at",
          orderDirection: "asc",
          conditions: [
            { key: "body.점검일", operator: ">=", value: startDate },
            { key: "body.점검일", operator: "<=", value: endDate },
            { key: "body.관리감독자정보.postId", operator: "IN", value: supervisorIds },
          ],
          withMeta: true,
          withoutRelation: true,
          onlyMeta: true,
          metaKeys: ["body.관리감독자정보.postId", "body.점검일", "body.점검시간", "body.작성일"],
        });
        const data = res.data || [];
        data.forEach((item) => {
          const content = item.postContents ? item.postContents : JSON.parse(item.post_content);
          const body = content.body;
          allDocs.push({
            postId: item.id,
            boardId: item.board_id,
            connect738PostId: (body.관리감독자정보 && body.관리감독자정보.postId) || null,
            점검일: body.점검일 || "",
            점검시간: body.점검시간 || "",
            작성일: body.작성일 || "",
          });
        });
        if (page >= (res.last_page || 1)) break;
        page++;
      }
      this.inspectDocs = allDocs;
    },

    // ─── 셀 헬퍼 ─────────────────────────────────────────────────
    getCellDocs(sup, day) {
      const byDay = this.docMap[sup.postId] || {};
      return byDay[day] || [];
    },

    getCellClass(sup, day) {
      const count = this.getCellDocs(sup, day).length;
      if (count === 0) return "cell-empty";
      return "cell-written";
    },

    getDayClass(day) {
      const date = new Date(this.appliedYear, this.appliedMonth - 1, day);
      const dow = date.getDay();
      if (dow === 0) return "day-sun";
      if (dow === 6) return "day-sat";
      return "";
    },

    // ─── 셀 클릭 ─────────────────────────────────────────────────
    onClickCell(sup, day) {
      const docs = this.getCellDocs(sup, day);

      if (docs.length === 0) {
        this.newDocTarget = { sup, day };
        this.showNewDocPopup = true;
      } else {
        this.selectedDocs = docs;
        this.showDocListPopup = true;
      }
    },

    openNewDoc() {
      if (!this.newDocTarget) return;
      const { sup, day } = this.newDocTarget;
      const 점검일 = `${this.appliedYear}-${String(this.appliedMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const base = `${window.location.origin}/project/${this.projectId}/tab/1140/write`;
      const query = `?connect738PostId=${sup.postId}&점검일=${점검일}`;
      const win = window.open(base + query, "_blank", `width=${screen.width},height=${screen.height}`);
      if (win) win.opener = null;
      this.showNewDocPopup = false;
      this.newDocTarget = null;
    },

    openDoc(doc) {
      const url = `${window.location.origin}/project/${this.projectId}/tab/1140/write/${doc.postId}`;
      const win = window.open(url, "_blank", `width=${screen.width},height=${screen.height}`);
      if (win) win.opener = null;
      this.showDocListPopup = false;
    },

    // ─── 페이징 ──────────────────────────────────────────────────
    async onChangePage(p) {
      if (this.page === p) return;
      this.page = p;
      this.isLoading = true;
      try {
        await this.loadSupervisors();
        await this.loadInspectDocs();
      } finally {
        this.isLoading = false;
      }
    },
    async onChangePerPage() {
      if (this.isLoading) return;
      this.page = 1;
      this.isLoading = true;
      try {
        await this.loadSupervisors();
        await this.loadInspectDocs();
      } finally {
        this.isLoading = false;
      }
    },

    // ─── 드래그 스크롤 ───────────────────────────────────────────
    onDragStart(e) {
      if (e.button !== 0) return;
      this.isDragging = true;
      this.dragStartX = e.pageX - this.$refs.scrollContainer.offsetLeft;
      this.dragScrollLeft = this.$refs.scrollContainer.scrollLeft;
    },
    onDragMove(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      const x = e.pageX - this.$refs.scrollContainer.offsetLeft;
      const walk = (x - this.dragStartX) * 1.2;
      this.$refs.scrollContainer.scrollLeft = this.dragScrollLeft - walk;
    },
    onDragEnd() {
      this.isDragging = false;
    },

    // ─── Excel 다운로드 ───────────────────────────────────────────
    async loadAllSupervisors() {
      const allSupervisors = [];
      let page = 1;
      while (true) {
        const conditions = [
          { key: "header.year", operator: "=", value: this.appliedYear },
          { key: "body.관리감독자정보.구분", operator: "!=", value: "관리직" },
        ];
        if (this.search.field && this.search.field.fieldId) {
          conditions.push({ key: "body.stdInfo.field_id", operator: "=", value: this.search.field.fieldId });
        }
        if (this.search.이름) {
          conditions.push({ key: "body.관리감독자정보.이름", operator: "LIKE", value: `%${this.search.이름}%` });
        }
        if (this.search.keyword) {
          conditions.push({ key: "body.관리감독자정보.이름", operator: "LIKE", value: `%${this.search.keyword}%`, boolean: "OR" });
          conditions.push({ key: "body.stdInfo.field_name", operator: "LIKE", value: `%${this.search.keyword}%`, boolean: "OR" });
          conditions.push({ key: "body.관리감독자정보.직무", operator: "LIKE", value: `%${this.search.keyword}%`, boolean: "OR" });
          conditions.push({ key: "body.관리감독자정보.부서명", operator: "LIKE", value: `%${this.search.keyword}%`, boolean: "OR" });
        }
        const res = await this.$store.dispatch("board/LOAD_POST_LIST", {
          projectId: this.projectId,
          boardId: 738,
          boardSlug: "board-738",
          page,
          perPage: 100,
          orderTarget: "created_at",
          orderDirection: "asc",
          conditions,
        });
        const data = res.data || [];
        data.forEach((item) => {
          const body = JSON.parse(item.post_content).body;
          const info = body.관리감독자정보 || {};
          const sup = {
            postId: item.id,
            사업장명: (body.stdInfo && body.stdInfo.field_name) || "",
            이름: info.이름 || "",
            직무: info.직무 || "",
            직급: info.직급 || "",
            부서명: info.부서명 || "",
          };
          allSupervisors.push(sup);
        });
        if (page >= (res.last_page || 1)) break;
        page++;
      }
      return allSupervisors;
    },

    async onClickExcel() {
      this.loading(true);
      const allSupervisors = await this.loadAllSupervisors();
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(`${this.appliedMonth}월`);

      // 컬럼 너비 설정
      const colLetter = (n) => {
        let r = "";
        while (n > 0) {
          n--;
          r = String.fromCharCode(65 + (n % 26)) + r;
          n = Math.floor(n / 26);
        }
        return r;
      };
      worksheet.getColumn("A").width = 6;
      worksheet.getColumn("B").width = 14;
      worksheet.getColumn("C").width = 10;
      worksheet.getColumn("D").width = 10;
      for (let i = 0; i < this.days.length; i++) {
        worksheet.getColumn(colLetter(5 + i)).width = 5;
      }

      const lastDayCol = colLetter(4 + this.days.length);

      // ── 헤더 1행: 빈칸 + 월 ──
      excelSupporter.addHeader(worksheet, [
        { value: "", style: "borderHead", merge: "A:A" },
        { value: "", style: "borderHead", merge: "B:B" },
        { value: "", style: "borderHead", merge: "C:C" },
        { value: "", style: "borderHead", merge: "D:D" },
        { value: `${this.appliedMonth}월`, style: "borderHead", merge: `E:${lastDayCol}` },
      ]);

      // ── 헤더 2행: 고정컬럼명 + 날짜 ──
      excelSupporter.addHeader(worksheet, [
        { value: "No.", style: "borderHead", merge: "A:A" },
        { value: "사업장명", style: "borderHead", merge: "B:B" },
        { value: "이름", style: "borderHead", merge: "C:C" },
        { value: "직급", style: "borderHead", merge: "D:D" },
        ...this.days.map((d, i) => ({
          value: d,
          style: "borderHead",
          merge: `${colLetter(5 + i)}:${colLetter(5 + i)}`,
        })),
      ]);

      // ── colgroup ──
      const colgroup = ["A:A", "B:B", "C:C", "D:D", ...this.days.map((_, i) => `${colLetter(5 + i)}:${colLetter(5 + i)}`)];

      // ── 데이터 행 ──
      const tableData = allSupervisors.map((sup, idx) => [
        String(idx + 1),
        sup.사업장명,
        sup.이름,
        sup.직급,
        ...this.days.map((d) => {
          const count = this.getCellDocs(sup, d).length;
          return count === 0 ? "●" : String(count);
        }),
      ]);

      excelSupporter.addTable(worksheet, {
        rowHeight: 18,
        colgroup,
        data: tableData,
      });

      // ── 다운로드 ──
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `관리감독자_점검실적_${this.appliedYear}_${this.appliedMonth}월.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      this.loading(false);
    },

    loading(open) {
      window.dispatchEvent(new CustomEvent("loadingDialog", { detail: { open } }));
    },

    // ─── 인쇄 ─────────────────────────────────────────────────────
    async onClickPrint() {
      this.loading(true);
      const allSupervisors = await this.loadAllSupervisors();
      this.loading(false);

      const filterInfo = [this.search.field && this.search.field.fieldName ? this.search.field.fieldName : "", `${this.appliedYear}년 ${this.appliedMonth}월`, this.search.이름 || ""].filter(Boolean).join(", ");

      const colLetterFn = (n) => {
        let r = "";
        while (n > 0) {
          n--;
          r = String.fromCharCode(65 + (n % 26)) + r;
          n = Math.floor(n / 26);
        }
        return r;
      };

      const thDays = this.days
        .map((d) => {
          const date = new Date(this.appliedYear, this.appliedMonth - 1, d);
          const dow = date.getDay();
          const color = dow === 0 ? "#e53e3e" : dow === 6 ? "#3182ce" : "#3c3c3c";
          return `<th style="color:${color}; width:22px;">${d}</th>`;
        })
        .join("");

      const rows = allSupervisors
        .map((sup, idx) => {
          const cells = this.days
            .map((d) => {
              const count = this.getCellDocs(sup, d).length;
              const text = count === 0 ? "●" : String(count);
              const color = count === 0 ? "#e53e3e" : "#200c63";
              return `<td style="color:${color};">${text}</td>`;
            })
            .join("");
          return `<tr>
          <td>${idx + 1}</td>
          <td>${sup.사업장명}</td>
          <td>${sup.이름}</td>
          <td>${sup.직급}</td>
          ${cells}
        </tr>`;
        })
        .join("");

      const logoUrl = `${window.location.origin}/img/동국제강_로그인.svg`;

      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>관리감독자 점검실적 모니터링</title>
  <style>
    @page { size: A3 landscape; margin: 10mm; }
    body { font-family: "Noto Sans KR", sans-serif; font-size: 9px; color: #1a1a1a; margin: 0; }
    .page-header { display: flex; align-items: center; position: relative; margin-bottom: 16px; }
    .logo { height: 28px; }
    .page-title { position: absolute; left: 50%; transform: translateX(-50%); font-size: 18px; font-weight: 700; white-space: nowrap; }
    .filter-info { margin-left: auto; font-size: 10px; color: #555; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 8px; }
    th, td { border: 1px solid #ccc; padding: 3px 2px; text-align: center; vertical-align: middle; overflow: hidden; white-space: nowrap; }
    th { background: #f0f0f0; font-weight: 600; }
    .month-th { background: #e8e8f0; font-size: 10px; font-weight: 700; }
    col.fixed { width: 50px; }
    col.day { width: 22px; }
  </style>
</head>
<body>
  <div class="page-header">
    <img src="${logoUrl}" class="logo" onerror="this.style.display='none'" />
    <span class="page-title">관리감독자 점검실적 모니터링</span>
    <span class="filter-info">${filterInfo}</span>
  </div>
  <table>
    <colgroup>
      <col style="width:32px"/><col style="width:64px"/><col style="width:56px"/><col style="width:48px"/>
      ${this.days.map(() => '<col style="width:22px"/>').join("")}
    </colgroup>
    <thead>
      <tr>
        <th rowspan="2">No.</th>
        <th rowspan="2">사업장명</th>
        <th rowspan="2">이름</th>
        <th rowspan="2">직급</th>
        <th colspan="${this.days.length}" class="month-th">${this.appliedMonth}월</th>
      </tr>
      <tr>${thDays}</tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
</body>
</html>`;

      const win = window.open("", "_blank", `width=${screen.width},height=${screen.height}`);
      win.document.write(html);
      win.document.close();
      win.onload = () => {
        win.print();
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.overflow-x-auto {
  cursor: grab;
  user-select: none;

  &.is-dragging {
    cursor: grabbing;
  }
}

.label-text {
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.monitor-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  font-size: 12px;

  th,
  td {
    border-right: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
    padding: 6px 4px;
    text-align: center;
    vertical-align: middle;
  }

  thead th {
    border-top: 1px solid #e0e0e0;
  }

  td:first-child,
  th:first-child {
    border-left: 1px solid #e0e0e0;
  }

  th {
    background: #f5f5f5;
    font-weight: 500;
    white-space: nowrap;
  }

  /* 고정 컬럼 sticky */
  .sticky-col {
    position: sticky;
    z-index: 2;
    background: #f5f5f5;
  }

  td.sticky-col {
    background: #fff;
  }

  .sticky-col-0 {
    left: 0;
    width: 50px;
    min-width: 50px;
  }
  .sticky-col-1 {
    left: 50px;
    width: 100px;
    min-width: 100px;
  }
  .sticky-col-2 {
    left: 150px;
    width: 80px;
    min-width: 80px;
  }
  .sticky-col-3 {
    left: 230px;
    width: 70px;
    min-width: 70px;
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.08);
  }

  .month-header {
    background: #ededf5;
    font-size: 13px;
    font-weight: 600;
  }

  .day-th {
    padding: 6px 2px;
    font-size: 11px;
  }

  .day-sun {
    color: #e53e3e;
    background: #fff5f5;
  }
  .day-sat {
    color: #3182ce;
    background: #ebf8ff;
  }

  .day-cell {
    cursor: pointer;
    padding: 4px 2px;
    font-size: 12px;
    font-weight: 500;
    transition: background 0.15s;

    &:hover {
      background: #f0f0f0;
    }

    &.cell-empty {
      .dot-empty {
        color: #e53e3e;
        font-size: 10px;
      }
    }

    &.cell-written {
      .doc-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #38a169;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        line-height: 1;
      }
    }
  }
}
</style>
